import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { useTitel } from "../titel";

/**
 * Der eigene Zugang: Anzeigename und Passwort.
 *
 * Damit braucht niemand mehr beim Besitzer anzurufen, um sein Passwort zu
 * wechseln — und der Besitzer muss keines kennen.
 */
export default function MeinZugang() {
  useTitel("Mein Zugang");
  const client = useQueryClient();
  const { data: ich } = useQuery({ queryKey: ["ich"], queryFn: api.ich, staleTime: 5 * 60_000 });

  const [name, setName] = useState<string | null>(null);
  const [alt, setAlt] = useState("");
  const [neu, setNeu] = useState("");
  const [wiederholung, setWiederholung] = useState("");
  const [pwFehler, setPwFehler] = useState<string | null>(null);
  const [pwFertig, setPwFertig] = useState(false);

  const nameSpeichern = useMutation({
    mutationFn: (wert: string) => api.ichAendern(wert),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ich"] });
      setName(null);
    },
  });

  const passwort = useMutation({
    mutationFn: () => api.passwortAendern(alt, neu),
    onSuccess: () => {
      setPwFertig(true);
      setAlt("");
      setNeu("");
      setWiederholung("");
    },
  });

  function passwortAbsenden(e: FormEvent) {
    e.preventDefault();
    setPwFehler(null);
    setPwFertig(false);
    if (neu !== wiederholung) {
      setPwFehler("Die beiden neuen Passwörter sind nicht gleich.");
      return;
    }
    if (neu.length < 10) {
      setPwFehler("Mindestens 10 Zeichen.");
      return;
    }
    passwort.mutate();
  }

  const feld =
    "mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none " +
    "focus:border-black/40 dark:border-white/15 dark:bg-neutral-800";

  const ROLLE: Record<string, string> = {
    besitzer: "Besitzer — sieht und ändert alles",
    bearbeiter: "Bearbeiter — darf anlegen und ändern",
    betrachter: "Betrachter — darf nur lesen",
  };

  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-semibold tracking-tight">Mein Zugang</h1>

      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
        <dt className="text-black/50 dark:text-white/50">Rolle</dt>
        <dd>{ich ? (ROLLE[ich.rolle] ?? ich.rolle) : "—"}</dd>
        <dt className="text-black/50 dark:text-white/50">Sichtbarkeit</dt>
        <dd>
          {ich?.maxBlutsgrad !== undefined
            ? `Verwandtschaft bis Grad ${ich.maxBlutsgrad}` +
              (ich.schwiegerSichtbar ? ", Angeheiratete inklusive" : ", ohne Angeheiratete")
            : "—"}
        </dd>
        {/* Beantwortet die Frage „warum darf ich das nicht ändern?" an der
            Stelle, an der sie aufkommt. */}
        <dt className="text-black/50 dark:text-white/50">Ändern</dt>
        <dd>
          {ich?.rolle === "besitzer"
            ? "alles"
            : ich?.rolle === "betrachter"
              ? "nichts — nur lesen"
              : [
                  ich?.bearbeitenVerstorbene ? "Verstorbene" : null,
                  (ich?.bearbeitenBlutsgrad ?? 0) > 0
                    ? `Lebende bis Grad ${ich?.bearbeitenBlutsgrad}`
                    : null,
                  "dich selbst und alles, was du selbst anlegst",
                ]
                  .filter(Boolean)
                  .join(", ")}
        </dd>
      </dl>

      {/* --- Anzeigename ---------------------------------------------- */}
      <section className="mt-8">
        <h2 className="font-medium">Anzeigename</h2>
        <p className="mt-1 text-sm text-black/50 dark:text-white/50">
          So steht dein Name bei allem, was du anlegst.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <input
            value={name ?? ich?.anzeigename ?? ""}
            onChange={(e) => setName(e.target.value)}
            className={feld + " max-w-xs"}
          />
          <button
            onClick={() => name !== null && nameSpeichern.mutate(name)}
            disabled={name === null || name.trim() === "" || nameSpeichern.isPending}
            className="mt-1 rounded-lg border border-black/15 px-3 py-2 text-sm hover:border-black/40
                       disabled:opacity-40 dark:border-white/15"
          >
            {nameSpeichern.isPending ? "speichert …" : "speichern"}
          </button>
        </div>
        {nameSpeichern.error && (
          <p className="mt-2 text-sm text-red-700">{(nameSpeichern.error as Error).message}</p>
        )}
      </section>

      {/* --- Passwort -------------------------------------------------- */}
      <section className="mt-8">
        <h2 className="font-medium">Passwort ändern</h2>
        <p className="mt-1 text-sm text-black/50 dark:text-white/50">
          Deine anderen Sitzungen (Handy, zweiter Browser) werden dabei abgemeldet. Diese hier
          bleibt angemeldet.
        </p>
        <form onSubmit={passwortAbsenden} className="mt-3 max-w-xs space-y-3">
          <label className="block">
            <span className="text-sm text-black/60 dark:text-white/60">bisheriges Passwort</span>
            <input
              type="password"
              autoComplete="current-password"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              className={feld}
            />
          </label>
          <label className="block">
            <span className="text-sm text-black/60 dark:text-white/60">neues Passwort</span>
            <input
              type="password"
              autoComplete="new-password"
              value={neu}
              onChange={(e) => setNeu(e.target.value)}
              className={feld}
            />
          </label>
          <label className="block">
            <span className="text-sm text-black/60 dark:text-white/60">noch einmal</span>
            <input
              type="password"
              autoComplete="new-password"
              value={wiederholung}
              onChange={(e) => setWiederholung(e.target.value)}
              className={feld}
            />
          </label>

          {pwFehler && <p className="text-sm text-red-700">{pwFehler}</p>}
          {passwort.error && (
            <p className="text-sm text-red-700">{(passwort.error as Error).message}</p>
          )}
          {pwFertig && <p className="text-sm text-green-700">Passwort geändert.</p>}

          <button
            type="submit"
            disabled={passwort.isPending || !alt || !neu}
            className="rounded-lg border border-black/15 px-3 py-2 text-sm hover:border-black/40
                       disabled:opacity-40 dark:border-white/15"
          >
            {passwort.isPending ? "ändert …" : "Passwort ändern"}
          </button>
        </form>
      </section>
    </div>
  );
}
